/**
 * HistoryBar — a visual 30-day health timeline rendered as colored bars.
 *
 * A presentational component: receives data via props and renders UI with no
 * knowledge of where the data came from. Uses useState to track hover state
 * and conditional rendering for the tooltip overlay.
 */
import React, { useState } from 'react';
import { DayHealth, statusToLabel } from '../types';
import { STATUS_COLORS, TOOLTIP_STYLES } from './constants';

const TOTAL_DAYS = 30;
const NO_DATA_COLOR = '#d5dbdb';

interface HistoryBarProps {
  history: DayHealth[];
  height: string;
}

export function HistoryBar({ history, height }: HistoryBarProps): JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);
  // Newer alarms may have fewer than 30 days of history. Take the most recent 30
  // entries and pad the left side with gray bars to always render a fixed-width bar.
  const clamped = history.slice(-TOTAL_DAYS);
  const emptyCount = Math.max(0, TOTAL_DAYS - clamped.length);

  return (
    <div>
      <div
        style={{ display: 'flex', gap: '2px', height, alignItems: 'stretch', position: 'relative' }}
      >
        {Array.from({ length: emptyCount }, (_, i) => (
          <div
            key={`empty-${i}`}
            data-testid="padding-bar"
            style={{
              flex: 1,
              backgroundColor: NO_DATA_COLOR,
              borderRadius: '2px',
            }}
          />
        ))}
        {clamped.map((day, i) => {
          const pct = ((emptyCount + i) / TOTAL_DAYS) * 100;
          return (
            <div
              key={day.date}
              data-testid="data-bar"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                flex: 1,
                backgroundColor: STATUS_COLORS[day.status],
                borderRadius: '2px',
                cursor: 'pointer',
                opacity: hovered === i ? 0.8 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              {hovered === i && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: `${pct}%`,
                    transform: `translateX(${pct < 10 ? '0%' : pct > 90 ? '-100%' : '-50%'})`,
                    marginBottom: '6px',
                    ...TOOLTIP_STYLES,
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{day.date}</div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}
                  >
                    <span style={{ color: STATUS_COLORS[day.status] }}>●</span>
                    {statusToLabel(day.status)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '4px',
          fontSize: '12px',
          color: 'var(--awsui-color-text-body-secondary)',
        }}
      >
        <span>30 days ago</span>
        <span>Today</span>
      </div>
    </div>
  );
}
